import { useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Stack,
  TextArea,
  TextField,
  useSnackbars,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import type { Tag } from '@customerio/hydra-ui-api';
import { GenerateDescriptionButton } from '../components/generate-description-button';
import { TagEditor } from '../components/tag-editor';
import { descriptionCounter, descriptionError } from '../utils/editing';
import type { DataIndexAnalyticsType } from '../utils/types';

/**
 * The description and tags of one item, changed together.
 *
 * The name is shown but not editable: it is what ingestion writes, and this
 * page describes it rather than renames it.
 *
 * Not the ADR-0001 form pattern. Of the two values here, only the description
 * is a field, and its one rule is the length Pluma already counts under it;
 * the tags are a picker that creates records of its own while it is open,
 * which is why the save waits on `isNaming` rather than on a schema. A Zod
 * resolver would own neither value. FPS-4010 revisits this when the drawer
 * grows the same editor.
 */

const SAVE_FAILED_MESSAGE = 'An error occurred when saving the details. Try again.';

export type EditDetailsModalData = {
  workspaceId: number;
  name: string;
  /** The description the item shows, which is what editing starts from. */
  description: string;
  tags: readonly Tag[];
  type: DataIndexAnalyticsType;
  /** Absent for a person attribute or an event, which belong to no object type. */
  objectTypeId?: number;
  /** Whether the viewer may change the tags, which needs its own permission. */
  canEditTags: boolean;
  /** Saves both halves; resolves to whether that worked. */
  onSave: (values: { description: string; tagIds: number[] }) => Promise<boolean>;
};

export function EditDetailsModal({ data, modalState, onClose }: PlumaManagedModalProps<EditDetailsModalData>) {
  const snackbars = useSnackbars();
  const [description, setDescription] = useState(data.description);
  const [tagIds, setTagIds] = useState(() => data.tags.map((tag) => tag.id));
  const [isSaving, setIsSaving] = useState(false);
  // A tag being named is not a tag yet, and saving through it would drop the
  // one the reader is in the middle of writing.
  const [isNaming, setIsNaming] = useState(false);

  const error = descriptionError(description);

  const save = async () => {
    if (error != null || isNaming) {
      return;
    }
    setIsSaving(true);
    const saved = await data.onSave({ description, tagIds });
    setIsSaving(false);

    if (saved) {
      onClose();
      return;
    }
    snackbars.create({ id: 'data-index-item.details-edit.error', variant: 'critical', message: SAVE_FAILED_MESSAGE });
  };

  return (
    <Modal state={modalState} title="Edit attribute details" onClose={onClose}>
      <ModalBody>
        <Stack gap="250">
          <TextField label="Name" value={data.name} isDisabled />

          <Stack gap="100">
            <TextArea
              label="Description"
              placeholder="Enter a description…"
              value={description}
              error={error}
              description={descriptionCounter(description)}
              rows={4}
              onInput={(event) => setDescription(event.target.value)}
            />
            <div>
              <GenerateDescriptionButton
                workspaceId={data.workspaceId}
                type={data.type}
                name={data.name}
                objectTypeId={data.objectTypeId}
                isDisabled={isSaving}
                onGenerated={setDescription}
              />
            </div>
          </Stack>

          <TagEditor
            workspaceId={data.workspaceId}
            tagIds={tagIds}
            tags={data.tags}
            canEdit={data.canEditTags}
            hasPermission={data.canEditTags}
            onSave={() => Promise.resolve(true)}
            bulk={{ tagIds, onChange: setTagIds, onNaming: setIsNaming }}
          />
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" isDisabled={isSaving} onClick={() => onClose()}>
          Cancel
        </Button>
        {/* Disabled only while a tag is half-named, which is not about the
            values in the form: the bulk edit's own bar does the same. */}
        <Button variant="primary" isDisabled={isNaming} isLoading={isSaving} onClick={() => void save()}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}
